import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { UploadDropzone } from '../Shared/UploadDropzone';
import { DocumentsList } from '../Shared/DocumentsList';
import { ExtractionResults } from '../Shared/ExtractionResults';
import { ROLES } from '../../../utils/rbac';
import './EvidenceStepperFlow.css';

export function EvidenceStepperFlow({
  step = 'documents',
  documents = [],
  reportType,
  currentUser,
  onUpload,
  onContinue,
  onBack,
  onBackToDocuments,
  onContinueToEvidence,
  isExtractionLoading = false,
}) {
  const { t } = useTranslation();
  const [manualTitle, setManualTitle] = useState('');
  const [manualDetails, setManualDetails] = useState('');
  const [manualEntries, setManualEntries] = useState([]);
  const canUpload = currentUser?.role === ROLES.ADMIN ||
    currentUser?.role === ROLES.SUPERADMIN ||
    currentUser?.role === ROLES.REVIEWER;
  const canContinue = documents.length > 0 && !isExtractionLoading;
  const canContinueToEvidence = Boolean(onContinueToEvidence) && !isExtractionLoading;
  const backHandler = onBackToDocuments || onBack;
  const showBack = typeof backHandler === 'function';
  const showContinueToEvidence = Boolean(onContinueToEvidence) && !isExtractionLoading;
  const shouldShowExtractionActions = showBack || showContinueToEvidence;
  const title = step === 'extraction'
    ? t('reports.workspace.stepper.extraction')
    : t('reports.workspace.stepper.documents');
  const suggestedDocuments = useMemo(() => {
    const base = {
      fascicolo: {
        title: t('reports.workspace.documentsDrawer.suggestedDocs.fascicolo.title'),
        description: t('reports.workspace.documentsDrawer.suggestedDocs.fascicolo.description'),
      },
      projectMap: {
        title: t('reports.workspace.documentsDrawer.suggestedDocs.projectMap.title'),
        description: t('reports.workspace.documentsDrawer.suggestedDocs.projectMap.description'),
      },
      year1Report: {
        title: t('reports.workspace.documentsDrawer.suggestedDocs.year1Report.title'),
        description: t('reports.workspace.documentsDrawer.suggestedDocs.year1Report.description'),
      },
      previousAnnual: {
        title: t('reports.workspace.documentsDrawer.suggestedDocs.previousAnnual.title'),
        description: t('reports.workspace.documentsDrawer.suggestedDocs.previousAnnual.description'),
      },
    };

    const suggestionsByType = {
      relazione_agronomica_primo_anno: [base.fascicolo, base.projectMap],
      relazione_agronomica_annuale: [base.fascicolo, base.projectMap, base.year1Report],
      plv_drop_report: [base.fascicolo, base.previousAnnual],
    };

    return suggestionsByType[reportType] || suggestionsByType.relazione_agronomica_primo_anno;
  }, [reportType, t]);

  const handleAddManualEntry = () => {
    if (!manualDetails.trim()) {
      return;
    }
    setManualEntries(prev => [
      ...prev,
      {
        id: `manual-${Date.now()}`,
        title: manualTitle.trim() || t('reports.workspace.documentsDrawer.manualFallbackTitle'),
        details: manualDetails.trim(),
      },
    ]);
    setManualTitle('');
    setManualDetails('');
  };

  const handleRemoveManualEntry = (id) => {
    setManualEntries(prev => prev.filter(entry => entry.id !== id));
  };

  return (
    <div className="evidence-stepper-flow">
      <div className="evidence-stepper-flow__header">
        <h2 className="evidence-stepper-flow__title">{title}</h2>
      </div>

      {step === 'documents' && (
        <div className="evidence-stepper-flow__section">
          {canUpload && (
            <div className="evidence-stepper-flow__block">
              <h3 className="evidence-stepper-flow__block-title">
                {t('reports.workspace.documentsDrawer.uploadTitle')}
              </h3>
              <UploadDropzone
                onUpload={onUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                maxFiles={10}
              />
            </div>
          )}

          <div className="evidence-stepper-flow__banner">
            <div className="evidence-stepper-flow__banner-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" fill="currentColor"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" fill="currentColor"/>
              </svg>
            </div>
            <div className="evidence-stepper-flow__banner-content">
              <h3 className="evidence-stepper-flow__banner-title">
                {t('reports.workspace.documentsDrawer.suggestedTitle')}
              </h3>
              <p className="evidence-stepper-flow__banner-hint">
                {t('reports.workspace.documentsDrawer.suggestedHint')}
              </p>
              <div className="evidence-stepper-flow__suggested-list">
                {suggestedDocuments.map((doc, index) => (
                  <div key={`${doc.title}-${index}`} className="evidence-stepper-flow__suggested-item">
                    <div className="evidence-stepper-flow__suggested-title">{doc.title}</div>
                    <div className="evidence-stepper-flow__suggested-note">{doc.description}</div>
                  </div>
                ))}
              </div>
              <p className="evidence-stepper-flow__email-note">
                {t('reports.workspace.documentsDrawer.emailNote')}
              </p>
            </div>
          </div>

          <div className="evidence-stepper-flow__block">
            <h3 className="evidence-stepper-flow__block-title">
              {t('reports.workspace.documentsDrawer.uploadedTitle')}
            </h3>
            <DocumentsList
              documents={documents}
              currentUser={currentUser}
            />
          </div>

          <div className="evidence-stepper-flow__block">
            <h3 className="evidence-stepper-flow__block-title">
              {t('reports.workspace.documentsDrawer.manualTitle')}
            </h3>
            <p className="evidence-stepper-flow__hint">
              {t('reports.workspace.documentsDrawer.manualHint')}
            </p>
            <div className="evidence-stepper-flow__manual-grid">
              <input
                className="evidence-stepper-flow__manual-input"
                value={manualTitle}
                onChange={(event) => setManualTitle(event.target.value)}
                placeholder={t('reports.workspace.documentsDrawer.manualTitlePlaceholder')}
                disabled={isExtractionLoading}
              />
              <textarea
                className="evidence-stepper-flow__manual-textarea"
                value={manualDetails}
                onChange={(event) => setManualDetails(event.target.value)}
                placeholder={t('reports.workspace.documentsDrawer.manualPlaceholder')}
                rows={4}
                disabled={isExtractionLoading}
              />
            </div>
            <div className="evidence-stepper-flow__manual-actions">
              <Button
                variant="secondary"
                onClick={handleAddManualEntry}
                disabled={!manualDetails.trim() || isExtractionLoading}
              >
                {t('reports.workspace.documentsDrawer.manualAdd')}
              </Button>
            </div>
            {manualEntries.length > 0 && (
              <div className="evidence-stepper-flow__manual-list">
                {manualEntries.map(entry => (
                  <div key={entry.id} className="evidence-stepper-flow__manual-item">
                    <div>
                      <div className="evidence-stepper-flow__manual-item-title">{entry.title}</div>
                      <div className="evidence-stepper-flow__manual-item-details">{entry.details}</div>
                    </div>
                    <button
                      type="button"
                      className="evidence-stepper-flow__manual-remove"
                      onClick={() => handleRemoveManualEntry(entry.id)}
                    >
                      {t('common.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="evidence-stepper-flow__actions">
            <Button
              variant="primary"
              onClick={onContinue}
              disabled={!canContinue}
            >
              {t('common.continue')}
            </Button>
          </div>
        </div>
      )}

      {step === 'extraction' && (
        <div className="evidence-stepper-flow__section">
          {isExtractionLoading ? (
            <div className="evidence-stepper-flow__loading">
              <Loader2 size={32} className="evidence-stepper-flow__spinner" />
              <p className="evidence-stepper-flow__loading-text">
                {t('reports.workspace.extractionProcessing')}
              </p>
            </div>
          ) : (
            <div className="evidence-stepper-flow__block">
              <h3 className="evidence-stepper-flow__block-title">
                {t('reports.workspace.documentsDrawer.resultsTitle')}
              </h3>
              <ExtractionResults documents={documents} />
            </div>
          )}
          {!isExtractionLoading && (
            <div className="evidence-stepper-flow__success">
              <h4>{t('reports.workspace.extractionSuccessTitle')}</h4>
              <p>{t('reports.workspace.extractionSuccessMessage')}</p>
            </div>
          )}

          {shouldShowExtractionActions && (
            <div className="evidence-stepper-flow__actions">
              {showBack && (
                <Button
                  variant="primary"
                  onClick={backHandler}
                  disabled={isExtractionLoading}
                >
                  {t('common.back')}
                </Button>
              )}
              {showContinueToEvidence && (
                <Button
                  variant="primary"
                  onClick={onContinueToEvidence}
                  disabled={!canContinueToEvidence}
                >
                  {t('common.continue')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

